import * as THREE from 'three';
import { damp, dampAngle, clamp, lerp, TAU, rr, rnd, pick, wrapAngle } from './util.js';
import { heightAt } from './terrain.js';
import { routeAt, ROUTE_LEN } from './layout.js';

const v3 = () => new THREE.Vector3();
// (azimuth offset, elevation offset) candidates for the chase cam, best-first
const VANTAGES = [[0, 0], [0, 0.28], [0.5, 0.12], [-0.5, 0.12], [0, 0.55], [0.95, 0.3], [-0.95, 0.3], [1.6, 0.4], [-1.6, 0.4], [0, 0.9]];

/**
 * Camera director: chase cam (default), auto cinematic shots, and a slow village overview.
 * Drag / wheel / pinch always work; in chase mode the camera drifts back behind the van when idle.
 */
export class CameraRig {
  constructor(camera, dom) {
    this.camera = camera;
    this.dom = dom;
    this.mode = 'follow';
    this.target = v3();
    this.lookAt = v3();
    this.az = 0.6;
    this.el = 0.42;
    this.dist = 22;
    this.userZoom = 1;
    this.idle = 10; // seconds since the user last touched the camera
    this.shot = null;
    this.shotT = 0;
    this.shotIdx = 0;
    this.fovT = 46;
    this.introT = 0;
    this._p = v3();
    this._a = v3();
    this._b = v3();
    this.shake = 0;
    this.obstacles = null;
    this.moved = 0; // pixels dragged during the current press (to tell clicks from drags)
    this.effDist = 20;
    this._bindInput();
  }

  setMode(m) {
    this.mode = m;
    this.idle = 10;
    if (m === 'cinematic') { this.shot = null; this.shotT = 0; }
    if (m === 'overview') { this.dist = 120; this.el = 0.6; }
    if (m === 'follow') { this.dist = 20; }
  }

  _bindInput() {
    const el = this.dom;
    let dragging = false, lx = 0, ly = 0;
    const pointers = new Map();
    let pinch0 = 0;
    el.addEventListener('pointerdown', (e) => {
      this.moved = 0;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      dragging = true; lx = e.clientX; ly = e.clientY;
      el.setPointerCapture?.(e.pointerId);
      if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        pinch0 = Math.hypot(a.x - b.x, a.y - b.y);
      }
    });
    el.addEventListener('pointermove', (e) => {
      if (!pointers.has(e.pointerId)) return;
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      if (pointers.size === 2) {
        const [a, b] = [...pointers.values()];
        const d = Math.hypot(a.x - b.x, a.y - b.y);
        if (pinch0) this.userZoom = clamp(this.userZoom * (pinch0 / d), 0.35, 3.2);
        pinch0 = d;
        this.idle = 0;
        return;
      }
      if (!dragging) return;
      const dx = e.clientX - lx, dy = e.clientY - ly;
      lx = e.clientX; ly = e.clientY;
      this.moved += Math.abs(dx) + Math.abs(dy);
      if (this.mode === 'cinematic') this.setMode('follow');
      this.az -= dx * 0.006;
      this.el = clamp(this.el + dy * 0.005, 0.06, 1.35);
      this.idle = 0;
    });
    const up = (e) => { pointers.delete(e.pointerId); if (!pointers.size) dragging = false; pinch0 = 0; };
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
    el.addEventListener('wheel', (e) => {
      e.preventDefault();
      this.userZoom = clamp(this.userZoom * Math.exp(e.deltaY * 0.0012), 0.35, 3.2);
      this.idle = Math.min(this.idle, 1.5);
    }, { passive: false });
  }

  setObstacles(grid) { this.obstacles = grid; }

  _blocked(x, z, r) {
    const g = this.obstacles;
    if (!g) return false;
    for (const [dx, dz] of [[0, 0], [r, 0], [-r, 0], [0, r], [0, -r], [r * 0.7, r * 0.7], [-r * 0.7, -r * 0.7], [r * 0.7, -r * 0.7], [-r * 0.7, r * 0.7]]) {
      if (g.at(x + dx, z + dz) > 0) return true;
    }
    return false;
  }

  /** Max distance from `origin` along (dx,dy,dz) before hitting a house / tree. */
  _clearDist(origin, dx, dy, dz, maxD) {
    const g = this.obstacles;
    if (!g) return maxD;
    const step = 1.6;
    for (let d = 3.5; d <= maxD; d += step) {
      const x = origin.x + dx * d, y = origin.y + dy * d, z = origin.z + dz * d;
      const h = g.at(x, z);
      if (h > 0 && y < heightAt(x, z) + h + 0.4) return Math.max(4.5, d - 2.2);
    }
    return maxD;
  }

  kick(a = 0.25) { this.shake = Math.max(this.shake, a); }

  _chooseShot(van, weasel) {
    const types = ['drone', 'roadside', 'lowchase', 'top', 'weasel', 'orbitvan'];
    let t = types[this.shotIdx++ % types.length];
    if (this.shotIdx > 1 && rnd() < 0.3) t = pick(types);
    const shot = { type: t, dur: rr(8, 12), t: 0, seed: rnd() * TAU };
    if (t === 'roadside') {
      for (let tries = 0; tries < 14; tries++) {
        const s = van.s + rr(20, 34);
        const p = routeAt(s);
        const side = rnd() < 0.5 ? 1 : -1;
        const off = rr(7, 12);
        const x = p.x + p.tz * side * off, z = p.z - p.tx * side * off;
        if (this._blocked(x, z, 3.2)) continue;
        shot.pos = new THREE.Vector3(x, heightAt(x, z) + rr(1.3, 3.0), z);
        break;
      }
      if (!shot.pos) { shot.type = 'orbitvan'; }
      shot.fov = rr(30, 38);
    }
    if (t === 'weasel') shot.side = rnd() < 0.5 ? 1 : -1;
    return shot;
  }

  update(dt, time, van, weasel) {
    const cam = this.camera;
    this.idle += dt;
    const focusVan = this._a.copy(van.pos);
    focusVan.y += 1.6;
    const wPos = weasel.head;
    const sep = Math.hypot(wPos.x - van.pos.x, wPos.z - van.pos.z);
    let fovT = 46;

    if (this.mode === 'follow') {
      // focus between van and weasel (weasel only if reasonably close)
      const w = sep < 30 ? 0.38 : 0;
      const f = this._b.set(lerp(focusVan.x, wPos.x, w), lerp(focusVan.y, wPos.y + 0.6, w), lerp(focusVan.z, wPos.z, w));
      this.target.x = damp(this.target.x, f.x, 4.5, dt);
      this.target.y = damp(this.target.y, f.y, 4.5, dt);
      this.target.z = damp(this.target.z, f.z, 4.5, dt);
      if (this.idle > 3.5) {
        const des = van.yaw + Math.PI + 0.5 + Math.sin(time * 0.06) * 0.35;
        this.az = dampAngle(this.az, des, 0.55, dt);
        this.el = damp(this.el, 0.46 + Math.sin(time * 0.05) * 0.06, 0.6, dt);
      }
      const aspect = this.camera.aspect;
      const fit = aspect < 1.2 ? 1 + (1.2 - aspect) * 0.75 : 1; // back off on tall / narrow screens
      const dDes = (12.5 + Math.min(sep, 24) * 0.5) * this.userZoom * fit;
      this.dist = damp(this.dist, dDes, 1.6, dt);
      fovT = 44 + Math.min(4, van.speed * 0.3);
      // find a vantage that is not blocked by houses/trees: try nudging azimuth + elevation first,
      // only shrink the distance as a last resort
      let best = null;
      for (const [da, de] of VANTAGES) {
        const az = this.az + da, el = clamp(this.el + de, 0.12, 1.3);
        const ce = Math.cos(el);
        const clear = this._clearDist(this.target, Math.sin(az) * ce, Math.sin(el), Math.cos(az) * ce, this.dist);
        if (clear >= this.dist * 0.93) { best = { az, el, clear }; break; }
        const score = clear - (Math.abs(da) * 2.5 + de * 4);
        if (!best || score > best.score) best = { az, el, clear, score };
      }
      this.viewAz = dampAngle(this.viewAz ?? this.az, best.az, 2.2, dt);
      this.viewEl = damp(this.viewEl ?? this.el, best.el, 2.2, dt);
      this.effDist = damp(this.effDist, Math.min(this.dist, best.clear), best.clear < this.effDist ? 9 : 1.6, dt);
      this._orbit(this.target, this.viewAz, this.viewEl, this.effDist);
    } else if (this.mode === 'overview') {
      this.target.x = damp(this.target.x, 0, 2, dt);
      this.target.y = damp(this.target.y, 4, 2, dt);
      this.target.z = damp(this.target.z, 0, 2, dt);
      if (this.idle > 2) this.az += dt * 0.05;
      this.dist = damp(this.dist, 105 * this.userZoom, 2, dt);
      this._orbit(this.target, this.az, this.el, this.dist);
      fovT = 42;
    } else {
      // cinematic
      if (!this.shot || this.shot.t > this.shot.dur) this.shot = this._chooseShot(van, weasel);
      const s = this.shot;
      s.t += dt;
      const p = this._p;
      const look = this._b;
      switch (s.type) {
        case 'drone': {
          const a = s.seed + time * 0.09;
          p.set(Math.cos(a) * 62, 0, Math.sin(a) * 62);
          p.y = 32 + Math.sin(time * 0.2) * 4;
          look.set(van.pos.x * 0.5, 2, van.pos.z * 0.5);
          fovT = 46;
          break;
        }
        case 'roadside': {
          p.copy(s.pos);
          look.copy(focusVan);
          look.lerp(wPos, 0.25);
          fovT = s.fov;
          break;
        }
        case 'lowchase': {
          const back = 8.5 + Math.sin(time * 0.4) * 1.0;
          p.set(van.pos.x - Math.sin(van.yaw) * back + Math.cos(van.yaw) * 3.5, 0, van.pos.z - Math.cos(van.yaw) * back - Math.sin(van.yaw) * 3.5);
          p.y = heightAt(p.x, p.z) + 1.6;
          look.set(lerp(focusVan.x, wPos.x, 0.3), focusVan.y - 0.2, lerp(focusVan.z, wPos.z, 0.3));
          fovT = 52;
          break;
        }
        case 'top': {
          p.set(van.pos.x - Math.sin(van.yaw) * 6, van.pos.y + 34, van.pos.z - Math.cos(van.yaw) * 6);
          look.copy(focusVan);
          fovT = 42;
          break;
        }
        case 'weasel': {
          const d = 6.5;
          const wy = weasel.yaw ?? 0;
          p.set(wPos.x + Math.sin(wy + s.side * 0.9) * d, 0, wPos.z + Math.cos(wy + s.side * 0.9) * d);
          p.y = heightAt(p.x, p.z) + 2.0;
          look.set(wPos.x, wPos.y + 0.6, wPos.z);
          fovT = 48;
          break;
        }
        default: { // orbitvan
          const a = s.seed + time * 0.28;
          p.set(van.pos.x + Math.cos(a) * 16, 0, van.pos.z + Math.sin(a) * 16);
          p.y = heightAt(p.x, p.z) + 6 + Math.sin(time * 0.5) * 1.5;
          look.copy(focusVan);
          fovT = 44;
        }
      }
      if (s.type !== 'drone' && s.type !== 'top' && this.obstacles) {
        // keep the lens out of houses and trees
        const dx = p.x - look.x, dy = p.y - look.y, dz = p.z - look.z;
        const len = Math.hypot(dx, dy, dz) || 1;
        const clear = this._clearDist(look, dx / len, dy / len, dz / len, len);
        if (clear < len) p.set(look.x + (dx / len) * clear, look.y + (dy / len) * clear, look.z + (dz / len) * clear);
      }
      // smooth into the shot for the first moments (soft cut)
      const k = 1 - Math.exp(-dt * (s.t < 0.6 ? 20 : 5));
      if (s.t < 0.05) { cam.position.copy(p); this.lookAt.copy(look); }
      cam.position.lerp(p, k);
      this.lookAt.lerp(look, 1 - Math.exp(-dt * 8));
      cam.lookAt(this.lookAt);
    }

    if (this.mode !== 'cinematic') {
      // keep the camera out of the ground
      const gy = heightAt(cam.position.x, cam.position.z);
      if (cam.position.y < gy + 1.4) cam.position.y = damp(cam.position.y, gy + 1.4, 12, dt);
      cam.lookAt(this.target);
    }

    // camera shake (honk / big events)
    if (this.shake > 0.001) {
      this.shake = damp(this.shake, 0, 5, dt);
      cam.position.x += Math.sin(time * 61) * this.shake * 0.08;
      cam.position.y += Math.sin(time * 53) * this.shake * 0.08;
    }
    this.fovT = fovT;
    cam.fov = damp(cam.fov, fovT, 2.5, dt);
    cam.updateProjectionMatrix();
  }

  _orbit(target, az, el, dist) {
    const cam = this.camera;
    const ce = Math.cos(el);
    const px = target.x + Math.sin(az) * ce * dist;
    const py = target.y + Math.sin(el) * dist;
    const pz = target.z + Math.cos(az) * ce * dist;
    if (this._first === undefined) { cam.position.set(px, py, pz); this._first = true; }
    // smooth positional follow (target already smoothed)
    cam.position.set(px, py, pz);
  }

  /** Where the shadow map / focus should be. */
  focusPoint(out, van) {
    return out.copy(this.mode === 'cinematic' ? van.pos : this.target);
  }
}
